import { hasLatin } from "@aybuben/core/writing/check.ts";
import { backspace, MAX_TYPED, typeText } from "@aybuben/core/writing/edit.ts";
import { keyAction, systemArmenian } from "@aybuben/core/writing/layouts.ts";
import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useStrings } from "../../i18n/index.tsx";
import { ARM, COL, FOCUS } from "../../theme.ts";
import { LessonDock } from "../dock.ts";
import { ownsKey } from "../keys.ts";
import { useWriting } from "./context.tsx";
import { Keyboard } from "./Keyboard.tsx";

interface TypedInputProps {
	/** Reports every change; the view turns it into its answer. */
	onChange: (text: string) => void;
	/**
	 * Enter in the device keyboard's input, which owns its keys: the shell
	 * checks, or continues once checked. Elsewhere the shell hears Enter itself.
	 */
	onSubmit: () => void;
	status: "right" | "wrong" | null;
}

/**
 * Where a writing step is typed: the in-app keyboard with its read-only field,
 * or a plain input for the device's own keyboard. Holds the text itself, so the
 * keyboard's held backspace, which fires from a timer, always edits the latest.
 */
export function TypedInput({ onChange, onSubmit, status }: TypedInputProps) {
	const t = useStrings();
	const w = useWriting();
	const [text, setText] = useState("");
	const locked = status !== null;
	// onChange is a fresh closure each render; only a change of text reports.
	const report = useRef(onChange);
	report.current = onChange;
	const first = useRef(true);
	useEffect(() => {
		if (first.current) {
			first.current = false;
			return;
		}
		report.current(text);
	}, [text]);

	const app = w.keys === "app";
	// Whether the system keyboard has shown itself Armenian; see keyAction.
	const armenianSystem = useRef(false);

	// A laptop's keys type through the in-app keyboard while it is open.
	useEffect(() => {
		if (!app || locked) return;
		const onKey = (e: KeyboardEvent) => {
			if (ownsKey(e.target, e.key)) return;
			armenianSystem.current = systemArmenian(e) ?? armenianSystem.current;
			const act = keyAction(e, w.layout, armenianSystem.current);
			if (!act) return;
			e.preventDefault();
			setText((s) => (act.kind === "backspace" ? backspace(s) : typeText(s, act.text)));
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [app, locked, w.layout]);

	const border = status === "right" ? COL.green : status === "wrong" ? COL.red : COL.line;
	const bg = status === "right" ? COL.greenSoft : status === "wrong" ? COL.redSoft : COL.card;
	const latin = !app && hasLatin(text);
	const dock = useContext(LessonDock);

	// The docked keyboard covers the foot of the page; on a short phone that
	// can be the field itself. Keep the field just above it as it grows.
	const fieldRef = useRef<HTMLElement | null>(null);
	useEffect(() => {
		if (!app || !dock || !fieldRef.current) return;
		const over =
			fieldRef.current.getBoundingClientRect().bottom + 8 - dock.el.getBoundingClientRect().top;
		if (over > 0) window.scrollBy({ top: over });
	}, [app, dock, text]);

	const swap = !locked && (
		<div className="mt-1 flex justify-center">
			<button
				type="button"
				onClick={() => w.setKeys(app ? "device" : "app")}
				className={`rounded-lg px-2 py-1 text-sm font-semibold ${FOCUS}`}
				style={{ color: COL.lapis }}
			>
				{t(app ? "write.useDevice" : "write.useApp")}
			</button>
		</div>
	);

	// Gone once checked: the feedback takes the foot of the screen.
	const keyboard = app && !locked && (
		<div
			className={dock ? "mx-auto pt-2" : "mt-3"}
			// Docked, 94% of the panel's width, as a phone's own keyboard is.
			// In place, 94% of a phone's width: the parent is that width less
			// the lesson's 20px gutters.
			style={dock ? { width: "94%" } : { marginInline: "calc(3% - 18.8px)" }}
		>
			{!w.hinted && (
				<div
					className="mb-2 rounded-2xl p-3 text-sm leading-snug"
					style={{ background: COL.lapisSoft, color: COL.lapisDark }}
				>
					{t(w.layout === "phonetic" ? "write.hintPhonetic" : "write.hintStandard")}
					<button
						type="button"
						onClick={w.dismissHint}
						className={`ml-2 rounded font-extrabold underline ${FOCUS}`}
					>
						{t("write.hintOk")}
					</button>
				</div>
			)}
			<Keyboard
				layout={w.layout}
				known={w.known}
				onType={(ch) => setText((s) => typeText(s, ch))}
				onBackspace={() => setText(backspace)}
			/>
			{swap}
		</div>
	);

	return (
		<div className="mt-6">
			{app ? (
				<div
					ref={(el) => {
						fieldRef.current = el;
					}}
					role="textbox"
					aria-label={t("write.field")}
					data-testid="typed-field"
					className="flex min-h-14 flex-wrap items-center rounded-2xl px-4 py-2"
					style={{ background: bg, border: `2px solid ${border}`, fontFamily: ARM, fontSize: 28 }}
				>
					<span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</span>
					{!locked && (
						<span aria-hidden="true" className="typed-caret" style={{ background: COL.lapis }} />
					)}
				</div>
			) : (
				<input
					type="text"
					lang="hy"
					aria-label={t("write.field")}
					data-testid="typed-field"
					placeholder={t("write.placeholder")}
					value={text}
					maxLength={MAX_TYPED}
					disabled={locked}
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="none"
					spellCheck={false}
					enterKeyHint="done"
					// The learner chose to type: a desktop keyboard should not need a click first.
					// oxlint-disable-next-line jsx-a11y/no-autofocus
					autoFocus
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							onSubmit();
						}
					}}
					className={`min-h-14 w-full rounded-2xl px-4 py-2 ${FOCUS}`}
					style={{
						background: bg,
						border: `2px solid ${border}`,
						fontFamily: ARM,
						fontSize: 28,
						color: COL.ink,
					}}
				/>
			)}

			{latin && (
				<p role="status" className="mt-2 text-sm font-semibold" style={{ color: COL.apricotDark }}>
					{t("write.latin")}
				</p>
			)}

			{app ? (dock && keyboard ? createPortal(keyboard, dock.el) : keyboard) : swap}
		</div>
	);
}
